/**
 * Iter 41.37 — "Request Client Creation" from a CRM lead.
 *
 * This dialog DOES NOT create a parallel request system. It thin-wraps the
 * existing `/api/admin/client-creation-requests` endpoint by linking the
 * originating lead and pre-filling the payload from the lead document.
 * Visible only when `pipeline_status ∈ {negotiation, won}`.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { suiteLabel } from '@/lib/crmSuites';

const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const fmtMoney = (n) => n == null ? '—' :
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

export default function RequestClientCreationDialog({ open, onOpenChange, lead, onSubmitted }) {
  const { t } = useTranslation();
  const [comments, setComments] = useState('');
  const [priority, setPriority] = useState('medium');
  const [saving, setSaving] = useState(false);

  if (!lead) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const doc = await adminApi.requestClientCreationFromLead(lead.id, {
        comments: comments.trim() || null,
        priority,
      });
      toast.success(t('pages.crm.requestClient.submitted', {
        defaultValue: 'Client creation request submitted',
      }));
      setComments('');
      setPriority('medium');
      onOpenChange(false);
      onSubmitted?.(doc);
    } catch (e) {
      toast.error(t('pages.crm.requestClient.failed', {
        defaultValue: 'Could not submit request',
      }), { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="request-client-creation-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('pages.crm.requestClient.title', { defaultValue: 'Request Client Creation' })}
          </DialogTitle>
          <DialogDescription>
            {t('pages.crm.requestClient.subtitle', {
              defaultValue:
                'Submit this lead to CTH Admin for approval. The existing client-creation workflow will be used.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <Row label={t('pages.crm.field.companyName')}      value={lead.company_name} />
          <Row label={t('pages.crm.field.primaryContact')}   value={lead.primary_contact_name} />
          <Row label={t('pages.crm.field.email')}            value={lead.email || '—'} />
          <Row label={t('pages.crm.field.country')}          value={lead.country || '—'} />
          <Row label={t('pages.crm.field.advisor')}          value={lead.assigned_advisor_id || t('pages.crm.unassigned', { defaultValue: 'Unassigned' })} />
          <Row label={t('pages.crm.field.estimatedValue')}   value={fmtMoney(lead.estimated_value)} />

          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
              {t('pages.crm.field.interestedSuites')}
            </p>
            <div className="flex flex-wrap gap-1" data-testid="rcc-suites">
              {(lead.interested_suites || []).map((s) => (
                <Badge key={s} variant="outline">{suiteLabel(s)}</Badge>
              ))}
              {(!lead.interested_suites || lead.interested_suites.length === 0) && (
                <span className="text-xs text-muted-foreground italic">
                  {t('pages.crm.requestClient.noSuites', {
                    defaultValue: 'No suites selected — admin will set on approval.',
                  })}
                </span>
              )}
            </div>
          </div>

          <div>
            <Label>{t('pages.requests.clientForm.priority', { defaultValue: 'Priority' })}</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="rcc-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {t(`pages.requests.priority.${p}`, { defaultValue: p })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="rcc-comments">
              {t('pages.crm.requestClient.commentsLabel', { defaultValue: 'Comments (optional)' })}
            </Label>
            <Textarea
              id="rcc-comments"
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={t('pages.crm.requestClient.commentsPh', {
                defaultValue: 'Anything the approving admin should know…',
              })}
              data-testid="rcc-comments"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
                  data-testid="rcc-cancel">
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving}
                  className="bg-gradient-primary hover:opacity-90"
                  data-testid="rcc-submit">
            {saving
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t('common.saving')}</>
              : t('pages.crm.requestClient.submit', { defaultValue: 'Submit Request' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
